import React, {useEffect, useRef, useState} from "react";
import {BaseGrid, FlexCol, FlexRow} from "./generic/Containers";
import {
    BCHeader1,
    BCHeader3,
    Subtitle1,
    Subtitle2,
    BCHeader5,
    Body2,
    Body3, Body1, BCHeader4
} from "./generic/BCHeaders";
import {
    BlogPreview,
    ChallengePreview,
    CoursePreview,
    GamePreview,
    ReferencePreview,
    ResourcePreview,
    ToolPreview,
    VideoPreview
} from "./DashboardPreviews";
import {REFERENCES_TYPES_CONFIG} from "../constants/ReferencesTypesConfig";
import {GAMES_TYPES_CONFIG} from "../constants/GamesTypesConfig";
import {CHALLENGES_TYPES_CONFIG} from "../constants/ChallengesTypesConfig";
import {TOOL_TYPES_CONFIG} from "../constants/ToolTypesConfig";
import {ALL_RESOURCES_LIST} from "../constants/ResourcesConfig";
import {ALL_CONTENT} from "../constants/LearningPathsConfig";
import {ARTICLES_MARKUP} from "../constants/ArticlesConfig";
import {BCIcon, ICONS} from "./generic/BCIcons";
import {useGlobalState} from "../GlobalState";
import {BCTertiaryButton, BCPrimaryButton} from "./generic/BCButtons";
import LinkedInLogo from "../images/experience/LinkedIn.webp";
import DeltaLogo from "../images/experience/Delta.webp";
import HomeDepotLogo from "../images/experience/HomeDepot.webp";
import DepartmentOfDefenseLogo from "../images/experience/DepartmentOfDefense.webp";
import UpsLogo from "../images/experience/UpsLogo.webp";
import DavidPhoto from "../images/experience/David.webp";
import BenPhoto from "../images/experience/Ben.webp";
import {Footer} from "./Footer";
import {
    HotOffThePressAndChallengesSection,
    FeaturedAPIsHighlight, DiscordComponent
} from "./HotOffThePressAndChallengesSection";
import {HeroBanner} from "./landingPage/HeroBanner";

interface SectionToggleMenuProps {
    sections: Array<{
        header?: string;
        title?: string;
        content?: any[];
        resources?: any[];
    }>;
    renderItem: (item: any, index: number) => React.ReactNode;
    setSelectedContent: any;
}

const SectionToggleMenu: React.FC<SectionToggleMenuProps> = ({ sections, renderItem, setSelectedContent }) => {
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(-1); // -1 represents "All"
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { isMobile } = useGlobalState();
    const dropdownRef = useRef<HTMLDivElement>(null);



    const getSectionHeader = (section: any): string => section.header || section.title;
    const getSectionContent = (section: any): any[] => section.content || section.resources;

    // Calculate total items for "All" tab
    const totalItems = sections.reduce((total, section) => total + getSectionContent(section).length, 0);

    // Get all items when "All" is selected
    const getAllItems = (): any[] => {
        return sections.flatMap(section => getSectionContent(section));
    };

    // Get the selected section name for dropdown display
    const getSelectedSectionName = (): string => {
        if (selectedSectionIndex === -1) {
            return `All (${totalItems})`;
        }
        const section = sections[selectedSectionIndex];
        return `${getSectionHeader(section)} (${getSectionContent(section).length})`;
    };

    const handleDropdownToggle = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleDropdownSelect = (index: number) => {
        setSelectedSectionIndex(index);
        setIsDropdownOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        if (isMobile && isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobile, isDropdownOpen]);

    return (
        <FlexCol gap={1}>
            {/* Section Toggle Buttons */}
            <FlexCol gap={.2}>
                <Subtitle2 text={"Categories"} className={"course-section-toggles-preface"} />
                
                {isMobile ? (
                    // Mobile Dropdown
                    <div className="course-section-dropdown-container" ref={dropdownRef}>
                        <button 
                            className="course-section-dropdown-button"
                            onClick={handleDropdownToggle}
                        >
                            <span>{getSelectedSectionName()}</span>
                            <BCIcon 
                                icon={isDropdownOpen ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN} 
                                className="dropdown-chevron"
                            />
                        </button>
                        
                        {isDropdownOpen && (
                            <div className="course-section-dropdown-menu">
                                {/* All option */}
                                <button 
                                    className={`course-section-dropdown-item ${selectedSectionIndex === -1 ? 'selected' : ''}`}
                                    onClick={() => handleDropdownSelect(-1)}
                                >
                                    All ({totalItems})
                                </button>
                                
                                {/* Individual section options */}
                                {sections.map((section, index) => (
                                    <button 
                                        key={index}
                                        className={`course-section-dropdown-item ${selectedSectionIndex === index ? 'selected' : ''}`}
                                        onClick={() => handleDropdownSelect(index)}
                                    >
                                        {getSectionHeader(section)} ({getSectionContent(section).length})
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    // Desktop Button Group
                    <FlexRow justify="center" className="course-section-toggles">
                        {/* All tab */}
                        <BCTertiaryButton
                            text={`All (${totalItems})`}
                            onClick={() => setSelectedSectionIndex(-1)}
                            isSelected={selectedSectionIndex === -1}
                            className="course-section-toggle"
                        />
                        <div className="toggle-menu-divider" />

                        {/* Individual section tabs */}
                        {sections.map((section, index) => (
                            <>
                                <BCTertiaryButton
                                    key={index}
                                    text={`${getSectionHeader(section)} (${getSectionContent(section).length})`}
                                    onClick={() => setSelectedSectionIndex(index)}
                                    isSelected={selectedSectionIndex === index}
                                    className="course-section-toggle"
                                />
                                {index < sections.length - 1 && (
                                    <div className="toggle-menu-divider" />
                                )}
                            </>
                        ))}
                    </FlexRow>
                )}
            </FlexCol>

            {/* Selected Section Content */}
            <FlexCol widthPercent={100} gap={1}>
                <BaseGrid gap={1} mobileColumns={1} useMinMax={true} columnGap={1} fixedItemWidth={true} widthPercent={100}>
                    {selectedSectionIndex === -1 
                        ? getAllItems().map((item, index) => renderItem(item, index))
                        : getSectionContent(sections[selectedSectionIndex]).map((item, index) => renderItem(item, index))
                    }
                </BaseGrid>
            </FlexCol>
        </FlexCol>
    );
};

export const DashboardMainMenu: React.FC<{setSelectedContent: any, navigate?: any, isEducator?: boolean}> = ({setSelectedContent, navigate, isEducator = false}) => {
    const { userProgressData, isAuthenticated, isPremium, setShowAuthModal, setAuthModalMode } = useGlobalState();

    return (
        <FlexCol gap={2} padding={0} heightPercent={100} widthPercent={100}>

            {/* Progress Overview */}
            {/*{isAuthenticated && (*/}
            {/*    <animated.div style={statsSpring}>*/}
            {/*        <FlexRow gap={2} justify="center" align="center" className="progress-overview-container">*/}
            {/*            <FlexCol align="center" gap={0.5}>*/}
            {/*                <div style={{ width: '80px', height: '80px' }}>*/}
            {/*                    <CircularProgressbar*/}
            {/*                        value={progressStats.progress}*/}
            {/*                        text={`${progressStats.progress}%`}*/}
            {/*                        styles={buildStyles({*/}
            {/*                            textSize: '16px',*/}
            {/*                            pathColor: 'var(--primary-blue)',*/}
            {/*                            textColor: 'var(--primary-blue)',*/}
            {/*                            trailColor: 'var(--color-gray-300)',*/}
            {/*                        })}*/}
            {/*                    />*/}
            {/*                </div>*/}
            {/*                <Subtitle2 text="Overall Progress" />*/}
            {/*            </FlexCol>*/}
            {/*            */}
            {/*            <FlexCol gap={0.5}>*/}
            {/*                <BCHeader3 text={`${progressStats.completedCourses}/${progressStats.totalCourses}`} />*/}
            {/*                <Subtitle2 text="Learning Paths Completed" />*/}
            {/*            </FlexCol>*/}
            {/*            */}
            {/*            <FlexCol gap={0.5}>*/}
            {/*                <BCHeader3 text={userProgressData?.totalTimeSpent || '0'} />*/}
            {/*                <Subtitle2 text="Hours Learning" />*/}
            {/*            </FlexCol>*/}
            {/*        </FlexRow>*/}
            {/*    </animated.div>*/}
            {/*)}*/}

            <FlexCol align={"center"} widthPercent={100} pHorizontal={2} alignSelf={"center"} gap={2}>
                <HeroBanner isEducator={false} />
                <ContentTypesOverview navigate={navigate} isEducator={false} />

                <HotOffThePressAndChallengesSection
                    navigate={navigate}
                    setSelectedContent={setSelectedContent}
                    isAuthenticated={isAuthenticated}
                    setShowAuthModal={setShowAuthModal}
                    setAuthModalMode={setAuthModalMode}
                />

                <FeaturedAPIsHighlight
                    setSelectedContent={setSelectedContent}
                    navigate={navigate}
                    isAuthenticated={isAuthenticated}
                    setShowAuthModal={setShowAuthModal}
                    setAuthModalMode={setAuthModalMode}/>

                <DiscordComponent isAuthenticated={isAuthenticated} setShowAuthModal={setShowAuthModal} setAuthModalMode={setAuthModalMode}/>

            </FlexCol>



            <Footer />

        </FlexCol>
    );
};

export const ContentTypesOverview: React.FC<{ navigate?: any, isEducator: boolean }> = ({ navigate, isEducator }) => {
    const { isAuthenticated, setShowAuthModal, setAuthModalMode, setShowContentOverviewModal, setContentOverviewModalType } = useGlobalState();

    const items: Array<{
        title: string;
        description: string;
        icon: any;
        target: string;
    }> = [
        { title: 'Courses', description: 'Structured paths to build real skills', icon: ICONS.LESSONS, target: 'courses' },
        { title: 'Interviews from Real Devs', description: 'Learn how others broke in', icon: ICONS.VIDEO, target: 'videos' },
        { title: 'Guides & Cheat Sheets', description: 'Quick references and how-tos', icon: ICONS.PDF, target: 'resources' },
        { title: 'Coding Challenges', description: 'Sharpen problem-solving', icon: ICONS.CHALLENGES, target: 'challenges' },
        { title: 'AI Job Tools', description: 'Leverage AI for your search', icon: ICONS.SPARKLE, target: 'tools' },
        { title: 'Mock Coding Interviews', description: 'Practice and improve', icon: ICONS.PLAY_CIRCLE, target: 'videos' },
    ];

    const handleNavigate = (target: string) => {
        if (isEducator) {
            // For educators, show content overview modal instead of navigating
            setContentOverviewModalType(target);
            setShowContentOverviewModal(true);
            return;
        }
        
        if (!isAuthenticated && (target === 'courses' || target === 'videos' || target === 'resources' || target === 'tools' || target === 'challenges')) {
            setAuthModalMode('register');
            setShowAuthModal(true);
            return;
        }
        if (navigate) {
            navigate(`/${target}`);
        }
    };

    return (
        <FlexCol gap={1.2} widthPercent={100} align="center" alignSelf={"center"} justify={"center"}>
            <FlexRow justify="center" widthPercent={100}>
            <BaseGrid gap={1} mobileColumns={1} tabletColumns={2} columns={3} widthPercent={100} justifyItems="stretch">
                {items.map((item, idx) => (
                    <FlexCol key={idx} gap={0.6} widthPercent={100} justify={"center"} align={"center"} alignSelf={"center"} padding={1} borderRadius={1} className={`card-container content-type-card ${isEducator ? 'educator-version' : ''}`} onClick={() => handleNavigate(item.target)} style={{ cursor: 'pointer' }}>
                        <FlexCol align="center" style={{ width: '100%' }}>
                            <div className="content-type-icon" style={{ width: '3em', height: '3em', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '999px', background: 'var(--blue-background)' }}>
                                <BCIcon icon={item.icon} style={{ color: 'var(--primary-blue)', width: '1.6em', height: '1.6em' }} />
                            </div>
                        </FlexCol>
                        <FlexRow align="center" gap={0.6} justify="center" widthPercent={100}>
                            <BCHeader4 text={item.title} textAlign="center" className="content-type-title" />
                        </FlexRow>
                        <Body2
                            text={item.description}
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}
                            textAlign="center"
                            className="content-type-desc"
                        />
                    </FlexCol>
                ))}
            </BaseGrid>
            </FlexRow>
        </FlexCol>
    );
};

export const LearnFromExperts: React.FC<{isEducator?: boolean}> = ({isEducator = false}) => {
    const {isAuthenticated, setShowAuthModal, setAuthModalMode, setShowEducatorInquiryModal} = useGlobalState();

    const experienceItems: Array<{ label: string; img: string }> = [
        {label: 'LinkedIn', img: LinkedInLogo},
        {label: 'Delta', img: DeltaLogo},
        {label: 'Home Depot', img: HomeDepotLogo},
        {label: 'US Government', img: DepartmentOfDefenseLogo},
        {label: 'UPS', img: UpsLogo}
    ];

    return (
        <FlexCol gap={1} mTop={2}>
            <FlexCol gap={0.8} align={"center"}>
                <BCHeader3 text="About Our Founders" textAlign="center" style={{fontSize: '1.6rem'}}/>
                <FlexRow className={'founders-content-container'} gap={1} justify={"center"} reverseMobile={true}
                         reverseTablet={true} maxWidth={80} mTop={1}>
                    <FlexRow className={'founders-images-container'} widthPercent={40} gap={1}>
                        <FlexCol align={"center"}>
                            <img src={DavidPhoto} alt="David"
                                 style={{width: '20em', objectFit: 'cover', borderRadius: '5%'}}/>
                        </FlexCol>
                        <FlexCol align={"center"}>
                            <img src={BenPhoto} alt="Ben"
                                 style={{width: '20em', objectFit: 'cover', borderRadius: '5%'}}/>
                        </FlexCol>
                    </FlexRow>

                    <FlexCol gap={0.8} widthPercent={60} className={"founders-content-text"} justify={"space-between"}>
                        <Body2 className={'founders-text'}
                            text="David and Ben founded Beyond Code with one mission: help new developers break into tech and build their careers. "
                            textAlign="left"
                        style={{fontSize: '1rem'}}/>
                        <Body2 className={'founders-text'}
                            text="Together, they have experience working from small 10-person startups up to some of the largest tech companies in the world. Their backgrounds span software engineering, career mentorship, and teaching aspiring developers at universities. "
                            textAlign="left"
                            style={{fontSize: '1rem'}}/>
                        {/*<Body2 text="They started Beyond Code after seeing a gap in the industry: while there are plenty of resources to learn coding, few focus on the real-world skills, career strategies, and insider knowledge needed to actually land a job. Beyond Code was built to close that gap." textAlign="left" />*/}
                        <Body2 className={'founders-text'} text="Companies they have worked for include:" textAlign="left"
                               style={{fontSize: '1rem'}}/>
                        <FlexRow gap={1} widthPercent={100} className={'founders-companies-container'}>
                            {experienceItems.map((item, index) => (
                                <img
                                    className={'founders-companies-icon'}
                                    src={item.img}
                                    alt={`${item.label} logo`}
                                    style={{width: '50px', height: '50px', objectFit: 'contain', borderRadius: '5%'}}
                                />
                            ))}
                        </FlexRow>
                    </FlexCol>
                </FlexRow>
            </FlexCol>

            {!isAuthenticated && (
                <FlexRow justify="center" mTop={4}>
                    <BCPrimaryButton
                        className={'dashboard-primary-cta'}
                        text={isEducator ? "Get Beyond Code for My Students" : "Build my Coding Career"}
                        iconRight={ICONS.RIGHT_ARROW}
                        onClick={() => {
                            if (isEducator) {
                                setShowEducatorInquiryModal(true);
                            } else {
                                setAuthModalMode('register');
                                setShowAuthModal(true);
                            }
                        }}
                    />
                </FlexRow>
            )}
        </FlexCol>
    );
};

export const AboutMeSection: React.FC = () => {
    return (
        <FlexRow gap={1} mTop={2} padding={2} className={'about-me-section-container'}>
            <FlexCol>
                <img src={DavidPhoto} alt="David" style={{width: '30em', objectFit: 'cover', borderRadius: '5%'}}/>
            </FlexCol>
            <FlexCol gap={0.8} >
                <BCHeader3 className={'about-me-header'} text="Hi, I'm David" style={{fontSize: '1.6rem'}}/>

                <Body2
                    className={'founders-text'}
                    text="I built Beyond Code with one mission: to help new developers break into tech and build real careers."
                    textAlign="left"
                    style={{fontSize: '1rem'}}
                />

                <Body2
                    className={'founders-text'}
                    text="I have been programming professionally for over 10 years. Like many new developers, I was largely self taught. I did not have a computer science degree or a clear roadmap. I had to learn how to stand out in the job process on my own."
                    textAlign="left"
                    style={{fontSize: '1rem'}}
                />

                <Body2
                    className={'founders-text'}
                    text="Along the way, I realized that getting hired is about more than just writing code. It is about resumes, portfolios, interviews, communication, and real world problem solving. These are the skills most coding courses never teach."
                    textAlign="left"
                    style={{fontSize: '1rem'}}
                />

                <Body2
                    className={'founders-text'}
                    text="Beyond Code exists to teach the practical, career focused knowledge I wish I had when I was starting out."
                    textAlign="left"
                    style={{fontSize: '1rem'}}
                />
            </FlexCol>
        </FlexRow>
    );
};

export const CoursesMainMenu: React.FC<{ setSelectedContent: any }> = ({setSelectedContent}) => {
    return (
        <SectionToggleMenu
            sections={ALL_CONTENT.allCourses}
            renderItem={(course, index) => (
                <CoursePreview key={index} course={course} setSelectedCourse={setSelectedContent}/>
            )}
            setSelectedContent={setSelectedContent}
        />
    );
};

export const VideosMainMenu: React.FC<{ setSelectedContent: any }> = ({setSelectedContent}) => {
    return (
        <SectionToggleMenu
            sections={ALL_CONTENT.allInterviews}
            renderItem={(video, index) => (
                <VideoPreview key={index} video={video} setSelectedContent={setSelectedContent}/>
            )}
            setSelectedContent={setSelectedContent}
        />
    );
};

export const ResourceMainMenu: React.FC<{ setSelectedContent: any }> = ({setSelectedContent}) => {
    return (
        <SectionToggleMenu
            sections={ALL_RESOURCES_LIST}
            renderItem={(resource, index) => (
                <ResourcePreview key={index} resource={resource} setSelectedResource={setSelectedContent}/>
            )}
            setSelectedContent={setSelectedContent}
        />
    );
};

export const ToolsMainMenu: React.FC<{ setSelectedContent: any }> = ({setSelectedContent}) => {
    return (
        <BaseGrid gap={1} mobileColumns={1} maxWidthEMs={50}>
            {TOOL_TYPES_CONFIG.map((tool, index) => (
                <ToolPreview key={index} tool={tool} setSelectedTool={setSelectedContent}/>
            ))}
        </BaseGrid>
    )
}

export const ReferencesMainMenu: React.FC<{ setSelectedContent: any }> = ({setSelectedContent}) => {
    return (
        <BaseGrid gap={1} columns={1} mobileColumns={1}>
            {REFERENCES_TYPES_CONFIG.map((reference, index) => (
                <ReferencePreview key={index} reference={reference} setSelectedResource={setSelectedContent}/>
            ))}
        </BaseGrid>
    )
}

export const GamesMainMenu: React.FC<{ setSelectedContent: any }> = ({setSelectedContent}) => {
    return (
        <BaseGrid gap={1} columns={1} mobileColumns={1}>
            {GAMES_TYPES_CONFIG.map((game, index) => (
                <GamePreview key={index} game={game} setSelectedGame={setSelectedContent}/>
            ))}
        </BaseGrid>
    )
}

export const ChallengesMainMenu: React.FC<{ setSelectedContent: any}> = ({setSelectedContent}) => {
    return (
        <BaseGrid gap={1} columns={1} mobileColumns={1}>
            {CHALLENGES_TYPES_CONFIG.map((challenge, index) => (
                <ChallengePreview key={index} challenge={challenge} setSelectedChallenge={setSelectedContent}/>
            ))}
        </BaseGrid>
    )
}

export const BlogMainMenu: React.FC<{setSelectedContent: any}> = ({setSelectedContent}) => {
    return (
        <BaseGrid gap={1} columns={3} mobileColumns={1}>
            {ARTICLES_MARKUP.map((blog, index) => (
                <BlogPreview blog={blog} setSelectedBlog={setSelectedContent}/>
            ))}
        </BaseGrid>
    )
}